
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import DashboardLink from '@/components/DashboardLink';

const NavBar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const onSubscription = location.pathname === '/subscription';
  const sectionHref = (hash: string) => (onSubscription ? `/${hash}` : hash);

  const scrollToPricing = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'py-3 cosmic-blur' : 'py-6 bg-transparent'
      }`}
      style={{ paddingTop: `calc(${scrolled ? '0.75rem' : '1.5rem'} + env(safe-area-inset-top))` }}
    >

      <div className="container mx-auto px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img 
            src="/images/mindmail-logo.png" 
            alt="MindMail Logo" 
            className="h-10 w-auto"
          />
        </Link>

        {/* Desktop Menu */}
        <nav className="hidden md:flex items-center space-x-8">
          <NavLink href={sectionHref("#features")}>Features</NavLink>
          <NavLink href={sectionHref("#book")}>Audiobook</NavLink>
          <NavLink href={sectionHref("#how-it-works")}>How It Works</NavLink>
          <NavLink href={sectionHref("#testimonials")}>Testimonials</NavLink>
          <Link to="/auth" className="cosmic-button-outline text-sm">
            Login
          </Link>
          {onSubscription ? (
            <a href="#pricing" onClick={scrollToPricing} className="cosmic-button text-sm">
              Get Started
            </a>
          ) : (
            <Link to="/subscription" className="cosmic-button text-sm">
              Get Started
            </Link>
          )}
        </nav>

        {/* Mobile Menu Toggle */}
        <button 
          className="md:hidden text-white focus:outline-none"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      <div 
        className={`md:hidden absolute top-full left-0 right-0 cosmic-blur backdrop-blur-lg transition-all duration-300 ease-in-out ${
          mobileMenuOpen ? 'max-h-screen py-4 opacity-100' : 'max-h-0 py-0 opacity-0 pointer-events-none'
        } overflow-hidden`}
      >
        <div className="container mx-auto px-6 flex flex-col space-y-4">
          <MobileNavLink href={sectionHref("#features")} onClick={() => setMobileMenuOpen(false)}>Features</MobileNavLink>
          <MobileNavLink href={sectionHref("#book")} onClick={() => setMobileMenuOpen(false)}>Audiobook</MobileNavLink>
          <MobileNavLink href={sectionHref("#how-it-works")} onClick={() => setMobileMenuOpen(false)}>How It Works</MobileNavLink>
          <MobileNavLink href={sectionHref("#testimonials")} onClick={() => setMobileMenuOpen(false)}>Testimonials</MobileNavLink>
          <Link
            to="/auth"
            className="text-white/90 text-lg font-medium py-2 block"
            onClick={() => setMobileMenuOpen(false)}
          >
            Login
          </Link>
          {onSubscription ? (
            <a href="#pricing" onClick={scrollToPricing} className="cosmic-button text-center py-2">
              Get Started
            </a>
          ) : (
            <Link
              to="/subscription"
              className="cosmic-button text-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Get Started
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

const NavLink = ({ href, children }: { href: string; children: React.ReactNode }) => {
  return (
    <a 
      href={href} 
      className="animated-underline text-white/90 text-sm font-medium"
    >
      {children}
    </a>
  );
};

const MobileNavLink = ({ href, children, onClick }: { href: string; children: React.ReactNode; onClick: () => void }) => {
  return (
    <a 
      href={href} 
      className="text-white/90 text-lg font-medium py-2 block"
      onClick={onClick}
    >
      {children}
    </a>
  );
};

export default NavBar;
